import gnbCircle from '@/assets/figma/gnb-circle.svg';
import logoDot from '@/assets/figma/logo-dot.svg';
import { AppBadge } from '@/shared/components/app-guide/AppBadge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@/shared/components/ui/sheet';
import { GNB_MENUS, ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { Link } from '@tanstack/react-router';
import { Menu } from 'lucide-react';
import { useState } from 'react';

const Logo = () => (
  <Link to={ROUTE_PATHS.ACCOUNTS} className="flex items-center gap-[7px]">
    <img src={logoDot} alt="" width={16} height={16} />
    <span className="text-[15px] leading-[23px] font-bold text-ink">
      RUDALINK
    </span>
  </Link>
);

/** M-13 A1 — 웹에 없는 메뉴는 숨기지 않고 회색 + [앱] 배지 + 팝오버 (클릭 · 호버 모두 연다) */
const MaxMatchMenu = () => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="flex items-center gap-1.5 text-sm leading-[21px] text-[#b9b9b9]"
      >
        맥스매치
        <AppBadge />
      </PopoverTrigger>
      <PopoverContent className="w-64">
        <p className="text-[13px] font-medium text-ink">
          맥스매치는 앱에서 이용할 수 있어요
        </p>
        <p className="mt-1 text-xs text-ink-5">
          처방 내역 기반 최적 의약품 매칭 기능입니다.
        </p>
      </PopoverContent>
    </Popover>
  );
};

const navLinkClass =
  'text-sm leading-[21px] text-ink-3 data-[status=active]:font-bold data-[status=active]:text-ink';

/** 아바타 메뉴에는 로그아웃만 둔다 (M-14: 마이페이지는 2차) */
const UserMenu = () => {
  const nickname = useAuthStore((s) => s.user?.nickname ?? '');
  const logout = useAuthStore((s) => s.logout);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-3 outline-none">
        <span className="flex size-7 items-center justify-center rounded-full bg-ink text-[11px] font-medium text-white">
          {nickname.slice(0, 1)}
        </span>
        <span className="hidden text-[13px] leading-5 text-ink-2 lg:inline">
          {nickname}
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => void logout()}>
          로그아웃
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Gnb = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-surface">
      <div className="mx-auto flex h-16 max-w-[1440px] items-center px-4 lg:px-10">
        {/* 모바일(<1024): 햄버거 메뉴 */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger
            className="mr-3 -ml-1 p-1 text-ink lg:hidden"
            aria-label="메뉴 열기"
          >
            <Menu className="size-5" />
          </SheetTrigger>
          <SheetContent side="left" overlay="popup" className="gap-1 p-5">
            <SheetTitle className="mb-4">
              <Logo />
            </SheetTitle>
            {GNB_MENUS.map((m) => (
              <Link
                key={m.to}
                to={m.to}
                onClick={() => setMobileOpen(false)}
                className={cn(navLinkClass, 'rounded-sm px-2 py-2.5')}
              >
                {m.label}
              </Link>
            ))}
            <div className="px-2 py-2.5">
              <MaxMatchMenu />
            </div>
          </SheetContent>
        </Sheet>

        <Logo />

        <nav className="hidden items-center gap-7 pl-10 lg:flex">
          {GNB_MENUS.map((m) => (
            <Link key={m.to} to={m.to} className={navLinkClass}>
              {m.label}
            </Link>
          ))}
          <MaxMatchMenu />
        </nav>

        <div className="flex-1" />

        <div className="flex items-center gap-3">
          {/* Figma 우측 원형 자리 (용도 미정 — 디자인 그대로 둠) */}
          <img
            src={gnbCircle}
            alt=""
            width={30}
            height={30}
            className="hidden lg:block"
          />
          <UserMenu />
        </div>
      </div>
    </header>
  );
};
